import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  stateHasFetchedAllObjectsSuccess,
} from 'utils/sliceHelpers';
import {
  EPermissionTypes,
  type IPermission,
  createPermission,
} from '@timeedit/types';
import api from '../services/api.service';

export const initialState: IDefaultAPIState<IPermission> = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

const slice = createSlice({
  name: 'assignablePermissions',
  initialState,
  reducers: {
    fetchAssignablePermissionsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAssignablePermissionsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<IPermission>>
    ) => {
      stateHasFetchedAllObjectsSuccess(payload, state, createPermission);
      finishedLoadingSuccess(state);
    },
    fetchAssignablePermissionsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

const selectAssignablePermissions = (state: TRootState) =>
  state.assignablePermissions.results;

export const assignableScopesSelector = createSelector(
  selectAssignablePermissions,
  (permissions) =>
    permissions.filter(
      (permission: IPermission) => permission.type === EPermissionTypes.SCOPE
    )
);

export const assignableAppPermissionsSelector = createSelector(
  selectAssignablePermissions,
  (permissions) =>
    permissions.filter(
      (permission: IPermission) =>
        permission.type === EPermissionTypes.APP_PERMISSION
    )
);

export const {
  fetchAssignablePermissionsRequest,
  fetchAssignablePermissionsSuccess,
  fetchAssignablePermissionsFailure,
} = slice.actions;

export const fetchAssignablePermissions = createAppAsyncThunk(
  'assignablePermissions/fetchAssignablePermissions',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAssignablePermissionsRequest());
      const result = (await api.get({
        endpoint: `/permissions?sortBy=name&assignableOnly=true`,
      })) as IDefaultAPIResult<IPermission>;
      dispatch(fetchAssignablePermissionsSuccess(result));
    } catch (e) {
      dispatch(fetchAssignablePermissionsFailure());
      console.error(e);
    }
  }
);
